import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { STUDY_TIMEPOINT_TYPES } from '@/clinical-study/vocabulary';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { apiClient } from '@/lib/api-client';
import { usePendingWrite } from '@/lib/use-pending-write';

const timepointRowSchema = z.object({
  timepointType: z.enum(STUDY_TIMEPOINT_TYPES),
  offsetDays: z.coerce.number().int('Deve essere un numero intero.'),
  toleranceWindowDaysBefore: z.coerce
    .number()
    .int('Deve essere un numero intero.')
    .nonnegative('Non può essere negativo.'),
  toleranceWindowDaysAfter: z.coerce
    .number()
    .int('Deve essere un numero intero.')
    .nonnegative('Non può essere negativo.'),
});
const timepointSetSchema = z.object({
  timepoints: z.array(timepointRowSchema).min(1, 'Almeno un timepoint è richiesto.'),
});
type TimepointSetFormInput = z.input<typeof timepointSetSchema>;
type TimepointSetFormOutput = z.output<typeof timepointSetSchema>;

/**
 * `StudyTimepointsSection` (FR-9.6/FR-9.7/FR-9.8) — this workspace's first `useFieldArray`
 * consumer and its first `<Input type="number">` consumer. The RHF three-generic `useForm<Input,
 * unknown, Output>` signature separates the pre-coercion string-carrying input shape (every
 * `<Input type="number">` is `register`ed as a plain uncontrolled string input) from the
 * post-`zodResolver` numeric output shape `handleSubmit`'s callback receives — `z.coerce.number()`
 * performs the conversion at submit time; `valueAsNumber` is deliberately NOT used on the same
 * fields, which would double-convert.
 *
 * The first row (index 0) is the fixed, non-removable Treatment anchor: its `timepointType`
 * renders as static text and its `offsetDays` renders `readOnly` — NEVER the HTML `disabled`
 * attribute or `register(..., { disabled: true })`, either of which EXCLUDES the field from the
 * submitted values (RHF's documented behavior), dropping `timepoints[0]` from the PUT body and
 * failing the server's own `superRefine` invariant on every legitimate save (BK-001). As
 * belt-and-braces, `onSubmit` also overrides the anchor's `timepointType`/`offsetDays` before
 * calling `apiClient.PUT`, mirroring the server's own invariant at the client boundary.
 */
export function StudyTimepointsSection({ studyId }: { studyId: string }) {
  const { t } = useTranslation('clinical-study');
  const [loadError, setLoadError] = useState(false);
  const [lockConflictError, setLockConflictError] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<TimepointSetFormInput, unknown, TimepointSetFormOutput>({
    resolver: zodResolver(timepointSetSchema),
  });
  const { pending, run } = usePendingWrite();
  const { fields, append, remove } = useFieldArray({ control, name: 'timepoints' });

  useEffect(() => {
    apiClient
      .GET('/v1/clinical-study/studies/{studyId}/timepoints', { params: { path: { studyId } } })
      .then(({ data, error, response }) => {
        if (error !== undefined || !response.ok || data === undefined) {
          setLoadError(true);
          return;
        }
        if (data.length === 0) {
          reset({
            timepoints: [
              {
                timepointType: 'TREATMENT_ANCHOR',
                offsetDays: 0,
                toleranceWindowDaysBefore: 0,
                toleranceWindowDaysAfter: 0,
              },
            ],
          });
          return;
        }
        reset({
          timepoints: data.map(
            ({
              timepointType,
              offsetDays,
              toleranceWindowDaysBefore,
              toleranceWindowDaysAfter,
            }) => ({
              timepointType,
              offsetDays,
              toleranceWindowDaysBefore,
              toleranceWindowDaysAfter,
            }),
          ),
        });
      })
      .catch(() => setLoadError(true));
  }, [studyId, reset]);

  const onSubmit = async (values: TimepointSetFormOutput) => {
    setSaved(false);
    setLockConflictError(null);
    setApiError(null);
    const body = {
      timepoints: values.timepoints.map((timepoint, index) =>
        index === 0
          ? { ...timepoint, timepointType: 'TREATMENT_ANCHOR' as const, offsetDays: 0 }
          : timepoint,
      ),
    };

    const { data, error, response } = await apiClient.PUT(
      '/v1/clinical-study/studies/{studyId}/timepoints',
      { params: { path: { studyId } }, body },
    );

    if (error !== undefined || !response.ok || data === undefined) {
      if (response.status === 409) {
        setLockConflictError(error?.detail ?? t('timepoints.lockConflictError'));
      } else {
        setApiError(error?.detail ?? t('writeError'));
      }
      return;
    }

    reset({
      timepoints: values.timepoints,
    });
    setSaved(true);
    toast.success(t('timepoints.savedToast'));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('timepoints.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadError && <p className="text-destructive text-sm">{t('timepoints.loadError')}</p>}
        {lockConflictError && <p className="text-destructive text-sm">{lockConflictError}</p>}
        {apiError && <p className="text-destructive text-sm">{apiError}</p>}
        <form
          className="space-y-4"
          onSubmit={(event) => void handleSubmit((values) => run(() => onSubmit(values)))(event)}
        >
          {fields.map((field, index) => {
            const isAnchor = index === 0;
            return (
              <div
                key={field.id}
                className="border-border space-y-3 border-t pt-4 first:border-t-0 first:pt-0"
              >
                {isAnchor ? (
                  <p className="text-sm font-medium">{t('timepoints.typeAnchor')}</p>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">
                      {t('timepoints.followUpRowLabel', { index })}
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-label={t('timepoints.removeRowLabel', { index })}
                      onClick={() => remove(index)}
                    >
                      {t('timepoints.removeRow')}
                    </Button>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="space-y-1">
                    <label
                      className="text-sm font-medium"
                      htmlFor={`timepoint-${index}-offset-days`}
                    >
                      {t('timepoints.offsetLabel')}
                    </label>
                    <Input
                      id={`timepoint-${index}-offset-days`}
                      type="number"
                      step="1"
                      readOnly={isAnchor}
                      {...register(`timepoints.${index}.offsetDays`)}
                    />
                    {errors.timepoints?.[index]?.offsetDays && (
                      <p className="text-destructive text-sm">
                        {errors.timepoints[index]?.offsetDays?.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label
                      className="text-sm font-medium"
                      htmlFor={`timepoint-${index}-tolerance-before`}
                    >
                      {t('timepoints.toleranceBeforeLabel')}
                    </label>
                    <Input
                      id={`timepoint-${index}-tolerance-before`}
                      type="number"
                      step="1"
                      {...register(`timepoints.${index}.toleranceWindowDaysBefore`)}
                    />
                    {errors.timepoints?.[index]?.toleranceWindowDaysBefore && (
                      <p className="text-destructive text-sm">
                        {errors.timepoints[index]?.toleranceWindowDaysBefore?.message}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label
                      className="text-sm font-medium"
                      htmlFor={`timepoint-${index}-tolerance-after`}
                    >
                      {t('timepoints.toleranceAfterLabel')}
                    </label>
                    <Input
                      id={`timepoint-${index}-tolerance-after`}
                      type="number"
                      step="1"
                      {...register(`timepoints.${index}.toleranceWindowDaysAfter`)}
                    />
                    {errors.timepoints?.[index]?.toleranceWindowDaysAfter && (
                      <p className="text-destructive text-sm">
                        {errors.timepoints[index]?.toleranceWindowDaysAfter?.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              append({
                timepointType: 'FOLLOW_UP',
                offsetDays: 1,
                toleranceWindowDaysBefore: 0,
                toleranceWindowDaysAfter: 0,
              })
            }
          >
            {t('timepoints.addRow')}
          </Button>

          <div>
            <Button type="submit" size="sm" disabled={!isDirty || pending}>
              {t('timepoints.saveSubmit')}
            </Button>
            {saved && <p className="text-success text-sm">{t('timepoints.saved')}</p>}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
